import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getAuth } from "firebase/auth";
import { getFirestore, collection, doc, serverTimestamp, query, where, getCountFromServer } from "firebase/firestore";
import { GENRES } from "../config/constants";
import toast from "react-hot-toast"; 
import { compressImage } from "../services/fileCompression";
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import Select from 'react-select'; 
import { addSongToPlatformCatalog } from "../services/catalogService";
import { useProfile } from "../context/ProfileContext";

const MusicUploadPage = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const artistProfile = state?.artistProfile;
  const { isDarkMode } = useProfile();

  // --- FORMS & FILE MEDIA STATES ---
  const [isUploading, setIsUploading] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [audioFile, setAudioFile] = useState(null);
  const [albumArtwork, setAlbumArtwork] = useState(null);
  const [artworkPreview, setArtworkPreview] = useState(null); 
  const [genres, setGenres] = useState([]);
  const [featuredArtists, setFeaturedArtists] = useState([]);

  // --- WORK PRODUCTION METADATA STATES ---
  const [composerName, setComposerName] = useState("");
  const [lyricistName, setLyricistName] = useState("");
  const [productionType, setProductionType] = useState("produced"); // 'produced' or 'licensed'
  const [licensePDF, setLicensePDF] = useState(null);
  const [finalWarrantyAccepted, setFinalWarrantyAccepted] = useState(false);

  // --- OTHER ATTRIBUTES ---
  const [isAIGenerated, setIsAIGenerated] = useState(false);
  const [hasExplicitLanguage, setHasExplicitLanguage] = useState(false);

  const auth = getAuth();
  const user = auth.currentUser;
  const db = getFirestore();

  const getAudioDuration = (file) => {
    return new Promise((resolve) => {
      const audio = new Audio();
      audio.src = URL.createObjectURL(file);
      audio.onloadedmetadata = () => {
        URL.revokeObjectURL(audio.src);
        resolve(Math.round(audio.duration)); 
      };
    });
  };

  const handleAudioFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size / 1024 / 1024 > 10) {
      toast.error("Audio file must be smaller than 10MB.");
      e.target.value = null; 
      return;
    }
    setAudioFile(file);
  };

  const handleArtworkFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size / 1024 / 1024 > 10) {
      toast.error("Album artwork must be smaller than 10MB.");
      e.target.value = null; 
      return;
    }
    try {
      const compressedFile = await compressImage(file, { maxSizeMB: 0.5 });
      setAlbumArtwork(compressedFile);
      setArtworkPreview(URL.createObjectURL(compressedFile));
    } catch (error) {
      toast.error("Failed to process artwork. Please try another file.");
    }
  };

  const handleUpload = async () => {
    if (isUploading) return;
    if (!user) return toast.error("User not authenticated.");
    
    if (!title || !audioFile || !genres.length || !albumArtwork || !composerName.trim() || !lyricistName.trim()) {
      return toast.error("Please fill in all required fields, including Composition Details.");
    }

    if (!finalWarrantyAccepted) {
      return toast.error("You must explicitly accept the content warranty to publish.");
    }

    setIsUploading(true);
    try {
      const duration = await getAudioDuration(audioFile);
      const artistId = artistProfile?.id;
      if (!artistId) throw new Error("Missing artist profile reference.");

      const token = await user.getIdToken();
      const storage = getStorage();
      
      const songsQuery = query(collection(db, 'songs'), where('artistId', '==', artistId));
      const countSnapshot = await getCountFromServer(songsQuery);
      const songCount = countSnapshot.data().count;

      const newSongRef = doc(collection(db, "songs"));
      const newSongId = newSongRef.id;

      let audioURL = null;
      let artworkURL = null;
      let licensePDFURL = null;

      const audioExtension = audioFile.name.split('.').pop();
      const audioFileName = `${newSongId}.${audioExtension}`;
      let generateUrlResponse = await fetch("https://us-central1-atomic-musicapp.cloudfunctions.net/generateArtistUploadUrl", {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ artistId, fileName: `music/${audioFileName}`, contentType: audioFile.type }),
      });

      if (!generateUrlResponse.ok) throw new Error('Could not get audio upload permission.');
      let { uploadUrl, finalPath } = await generateUrlResponse.json();
      await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': audioFile.type }, body: audioFile });
      audioURL = await getDownloadURL(ref(storage, finalPath));

      const artworkExtension = albumArtwork.name.split('.').pop();
      const artworkFileName = `${newSongId}.${artworkExtension}`;
      generateUrlResponse = await fetch("https://us-central1-atomic-musicapp.cloudfunctions.net/generateArtistUploadUrl", {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ artistId, fileName: `artwork/${artworkFileName}`, contentType: albumArtwork.type }),
      });
      if (!generateUrlResponse.ok) throw new Error('Could not get artwork upload permission.');
      ({ uploadUrl, finalPath } = await generateUrlResponse.json());
      await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': albumArtwork.type }, body: albumArtwork });
      artworkURL = await getDownloadURL(ref(storage, finalPath));

      if (productionType === 'licensed' && licensePDF) {
        generateUrlResponse = await fetch("https://us-central1-atomic-musicapp.cloudfunctions.net/generateArtistUploadUrl", {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ artistId, fileName: `licenses/${newSongId}_license.pdf`, contentType: licensePDF.type }),
        });
        if (generateUrlResponse.ok) {
          const pData = await generateUrlResponse.json();
          await fetch(pData.uploadUrl, { method: 'PUT', headers: { 'Content-Type': licensePDF.type }, body: licensePDF });
          licensePDFURL = await getDownloadURL(ref(storage, pData.finalPath));
        }
      }

      const { writeBatch } = await import("firebase/firestore"); 
      const batch = writeBatch(db);

      const randomSeed = Math.random();

      const songData = {
        title,
        title_lowercase: title.toLowerCase(), 
        description,
        duration,
        genres: genres.map(g => g.value),
        audioURL, 
        albumArtworkURL: artworkURL, 
        uploadedBy: user.uid,
        artistId: artistProfile.id,
        artistName: artistProfile.artistName,
        artistPhotoURL: artistProfile.photoURL,
        uploadedAt: serverTimestamp(),
        featuredArtists,
        isAIGenerated,
        isDeleted: false,
        hasExplicitLanguage,
        order: songCount,
        radioPlayCount: 0,         
        lastPlayedOnRadio: null,
        randomSeed,
        
        // Verification Parameters
        composerLegalName: composerName.trim(),
        lyricistLegalName: lyricistName.trim(),
        productionRightsType: productionType,
        licensePDFURL,
        legalWarrantyAccepted: true,
      };

      batch.set(newSongRef, songData);

      const aggregateRef = doc(db, "songAggregates", newSongId);
      batch.set(aggregateRef, {
        totalChartPoints: 100,
        pressBoostApplied: false,
        isDeleted: false, 
        title: songData.title,
        artistId: songData.artistId,
        artistName: songData.artistName,
        lastUpdated: serverTimestamp()
      }, { merge: true });

      await batch.commit();

      await addSongToPlatformCatalog({
        id: newSongId,
        artistId: songData.artistId,
        randomSeed: songData.randomSeed
      });

      toast.success("Music uploaded successfully!");
      navigate(`/profile`); 

    } catch (error) {
      toast.error("Upload failed: " + error.message);
    } finally {
      setIsUploading(false);
    }
  };

  const genreOptions = GENRES.map(g => ({ value: g, label: g }));

  const selectStyles = {
    control: (base) => ({ 
      ...base, 
      backgroundColor: 'transparent', 
      borderColor: 'transparent', 
      boxShadow: 'none',
      cursor: 'pointer'
    }),
    placeholder: (base) => ({
      ...base,
      color: isDarkMode ? 'rgba(255, 255, 255, 0.4)' : '#64748b',
      fontSize: '14px',
      fontWeight: '500'
    }),
    singleValue: (base) => ({
      ...base,
      color: isDarkMode ? '#ffffff' : '#0f172a'
    }),
    menu: (base) => ({ 
      ...base, 
      backgroundColor: isDarkMode ? '#18181b' : '#ffffff', 
      borderRadius: '1rem', 
      overflow: 'hidden',
      border: isDarkMode ? '1px solid #27272a' : '1px solid #e2e8f0',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
      zIndex: 50
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected 
        ? '#ff00cc' 
        : state.isFocused 
          ? (isDarkMode ? 'rgba(255, 0, 204, 0.15)' : 'rgba(255, 0, 204, 0.08)') 
          : 'transparent',
      color: state.isSelected 
        ? '#ffffff' 
        : isDarkMode ? '#f4f4f5' : '#1e293b',
      fontSize: '13px',
      fontWeight: '600',
      cursor: 'pointer'
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: isDarkMode ? '#27272a' : '#f1f5f9',
      borderRadius: '0.5rem',
      border: isDarkMode ? '1px solid #3f3f46' : '1px solid #cbd5e1'
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: isDarkMode ? '#f4f4f5' : '#0f172a',
      fontWeight: '700',
      fontSize: '12px'
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: isDarkMode ? '#a1a1aa' : '#64748b',
      ':hover': {
        backgroundColor: '#ff00cc',
        color: '#ffffff'
      }
    })
  };

  return (
    <div className="min-h-screen flex flex-col items-center bg-slate-50 dark:bg-brand-black text-slate-900 dark:text-brand-white p-4 transition-colors duration-500 relative">
      <div className="w-full max-w-lg bg-white dark:bg-brand-black p-6 md:p-8 rounded-3xl shadow-2xl border border-slate-100 dark:border-brand-gray mt-4">
        <h1 className="text-xl font-bold text-left mb-6">Upload New Track</h1>

        <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
          <input 
            type="text" 
            placeholder="Song Title" 
            value={title} 
            onChange={(e) => setTitle(e.target.value)} 
            className="w-full p-4 bg-slate-50 dark:bg-brand-gray/40 border border-slate-300 dark:border-brand-gray rounded-2xl dark:text-brand-white outline-none focus:ring-2 focus:ring-brand-pink" 
            required 
          />
          
          <textarea 
            placeholder="Description (optional)" 
            value={description} 
            onChange={(e) => setDescription(e.target.value)} 
            className="w-full p-4 bg-slate-50 dark:bg-brand-gray/40 border border-slate-300 dark:border-brand-gray rounded-2xl dark:text-brand-white outline-none focus:ring-2 focus:ring-brand-pink min-h-[90px] resize-none" 
          />

          {/* Audio & Artwork Inputs */}
          <div className="p-4 bg-slate-50 dark:bg-brand-gray/20 border border-slate-200 dark:border-brand-gray/60 rounded-2xl">
            <label className="block text-xs font-black uppercase tracking-wider text-gray-500 dark:text-brand-white/50 mb-2">Audio File</label>
            <input type="file" accept="audio/*" onChange={handleAudioFileChange} className="block w-full text-xs text-gray-500 dark:text-brand-white/40 file:mr-4 file:py-2 file:px-4 file:rounded-full file:bg-brand-pink/10 file:text-[#ff00cc] cursor-pointer" required />
          </div>

          <div className="p-4 bg-slate-50 dark:bg-brand-gray/20 border border-slate-200 dark:border-brand-gray/60 rounded-2xl">
            <label className="block text-xs font-black uppercase tracking-wider text-gray-500 dark:text-brand-white/50 mb-2">Album Artwork</label>
            <input type="file" accept="image/*" onChange={handleArtworkFileChange} className="block w-full text-xs text-gray-500 dark:text-brand-white/40 file:mr-4 file:py-2 file:px-4 file:rounded-full file:bg-brand-pink/10 file:text-[#ff00cc] cursor-pointer" required />
            {artworkPreview && <img src={artworkPreview} alt="Preview" className="w-20 h-20 rounded-2xl object-cover mt-3 border dark:border-brand-gray" />}
          </div>

          {/* WORK COMPOSITION METADATA LAYER */}
          <div className="p-4 bg-slate-50 dark:bg-brand-gray/20 border border-slate-200 dark:border-brand-gray/60 rounded-2xl space-y-3">
            <label className="block text-xs font-black uppercase tracking-wider text-[#ff00cc]">Work Composition (Legal Names)</label>
            <input type="text" placeholder="Composer (Full Legal Name)" value={composerName} onChange={(e) => setComposerName(e.target.value)} className="w-full p-3 bg-white dark:bg-brand-black border border-slate-200 dark:border-brand-gray rounded-xl text-sm text-slate-800 dark:text-brand-white outline-none focus:ring-1 focus:ring-brand-pink" required />
            <input type="text" placeholder="Lyricist (Full Legal Name)" value={lyricistName} onChange={(e) => setLyricistName(e.target.value)} className="w-full p-3 bg-white dark:bg-brand-black border border-slate-200 dark:border-brand-gray rounded-xl text-sm text-slate-800 dark:text-brand-white outline-none focus:ring-1 focus:ring-brand-pink" required />
          </div>

          {/* PRODUCTION RIGHTS METADATA LAYER */}
          <div className="p-4 bg-slate-50 dark:bg-brand-gray/20 border border-slate-200 dark:border-brand-gray/60 rounded-2xl space-y-3">
            <label className="block text-xs font-black uppercase tracking-wider text-[#ff00cc]">Production Rights</label>
            <div className="flex gap-2">
              <button 
                type="button" 
                onClick={() => setProductionType("produced")} 
                className={`flex-1 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider border transition-all ${
                  productionType === 'produced' 
                    ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900 border-slate-900 dark:border-white shadow-sm' 
                    : 'bg-white/60 dark:bg-white/5 border-slate-300 dark:border-zinc-700 text-slate-700 dark:text-zinc-300 hover:border-slate-400 dark:hover:border-zinc-500'
                }`}
              >
                I produced this beat
              </button>
              <button 
                type="button" 
                onClick={() => setProductionType("licensed")} 
                className={`flex-1 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider border transition-all ${
                  productionType === 'licensed' 
                    ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900 border-slate-900 dark:border-white shadow-sm' 
                    : 'bg-white/60 dark:bg-white/5 border-slate-300 dark:border-zinc-700 text-slate-700 dark:text-zinc-300 hover:border-slate-400 dark:hover:border-zinc-500'
                }`}
              >
                I have a License
              </button>
            </div>

            {productionType === 'licensed' && (
              <div className="pt-2 border-t border-dashed border-slate-200 dark:border-brand-gray/40 space-y-1.5">
                <label className="block text-[11px] font-bold text-slate-600 dark:text-zinc-400">Upload Beat License PDF (Optional)</label>
                <input type="file" accept=".pdf" onChange={(e) => setLicensePDF(e.target.files[0])} className="block w-full text-xs text-gray-500 dark:text-brand-white/40 file:mr-4 file:py-1.5 file:px-3 file:rounded-xl file:border-0 file:text-[11px] file:font-bold file:bg-slate-200 dark:file:bg-brand-gray text-slate-800 dark:text-brand-white cursor-pointer" />
              </div>
            )}
          </div>

          {/* GENRE SELECTOR */}
          <div className="w-full p-2 bg-slate-50 dark:bg-brand-gray/40 border border-slate-300 dark:border-brand-gray rounded-2xl">
            <Select 
              isMulti 
              options={genreOptions} 
              value={genres} 
              onChange={setGenres} 
              styles={selectStyles} 
              placeholder="Select genres..." 
              required 
            />
          </div>

          <div className="flex items-center p-4 bg-slate-50 dark:bg-brand-gray/20 border border-slate-200 dark:border-brand-gray/60 rounded-2xl">
            <input type="checkbox" id="explicitLanguageCheck" checked={hasExplicitLanguage} onChange={(e) => setHasExplicitLanguage(e.target.checked)} className="mr-3 h-4 w-4 text-brand-pink border-slate-300 dark:border-brand-gray rounded focus:ring-brand-pink bg-transparent" />
            <label htmlFor="explicitLanguageCheck" className="text-xs font-bold text-slate-700 dark:text-brand-white/80 select-none cursor-pointer">This song contains explicit language.</label>
          </div>

          <div className="flex items-center p-4 bg-slate-50 dark:bg-brand-gray/20 border border-slate-200 dark:border-brand-gray/60 rounded-2xl">
            <input type="checkbox" id="aiGeneratedCheck" checked={isAIGenerated} onChange={(e) => setIsAIGenerated(e.target.checked)} className="mr-3 h-4 w-4 text-brand-pink border-slate-300 dark:border-brand-gray rounded focus:ring-brand-pink bg-transparent" />
            <label htmlFor="aiGeneratedCheck" className="text-xs font-bold text-slate-700 dark:text-brand-white/80 select-none cursor-pointer">This audio track was wholly generated by an AI process.</label>
          </div>

          {/* EXPLICIT LEGAL WARRANTY REQUIREMENT */}
          <div className="p-4 bg-brand-pink/5 border border-[#ff00cc]/20 rounded-2xl flex items-start gap-3">
            <input 
              type="checkbox" 
              id="finalWarrantyCheck" 
              checked={finalWarrantyAccepted} 
              onChange={(e) => setFinalWarrantyAccepted(e.target.checked)} 
              className="mt-0.5 h-4 w-4 text-brand-pink border-slate-300 dark:border-brand-gray rounded focus:ring-brand-pink bg-transparent flex-shrink-0 cursor-pointer" 
            />
            <label htmlFor="finalWarrantyCheck" className="text-[11px] font-bold text-slate-700 dark:text-brand-white/80 leading-normal select-none cursor-pointer">
              I confirm that I am the authorized creator/owner of this sound recording and composition, and I grant Atomic Media a non-exclusive, promotional streaming licence to host this work interactively on the platform in accordance with the Terms of Service.
            </label>
          </div>

          {/* Upload Button */}
          <button
            type="button"
            onClick={handleUpload}
            disabled={isUploading || !finalWarrantyAccepted}
            className={`w-full py-3 px-4 rounded-xl font-black uppercase text-xs tracking-widest shadow-md transition-all active:scale-98 ${
              isUploading || !finalWarrantyAccepted
                ? "bg-slate-200 dark:bg-slate-800 text-slate-400 cursor-not-allowed shadow-none" 
                : "bg-brand-pink hover:brightness-95 text-white"
            }`}
          >
            {isUploading ? "Uploading..." : "Upload Music"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default MusicUploadPage;